import { message } from 'antd';
import { HttpStatusCode } from '@/fetch/core/constant';
import { mapEditorFetch } from '../utils/fetch';
import { useEditorStore } from '../store/editorStore';
import { useDataStore } from '../store/dataStore';
import { useSearchStore } from '../store/searchStore';
import {
  buildRecordListRequestKey,
  isSameCoordinate,
  isSameRecordListRequestKey,
  MapVariableListRequestKey,
} from '../utils';
import { MapStatus } from '../utils/config';
import type MapEngine from '@/map-core';
import { buildMapVariableCreatePayload } from '../utils/mapVariable';

/**
 * 数据同步管理器
 *
 * 层级：controller（"业务怎么做"）
 *
 * 职责：
 * 1. 数据请求 — fetchList 带 inFlight/lastSuccess 双 key 去重 + token 竞态处理
 * 2. CRUD 操作 — create / edit / remove / toggleStatus，每步含 API 调用 + store 回写 + 列表刷新
 *
 * 通过回调（resetInteractionState / highlightMap）与 Controller 协作，
 * 不直接引用 Controller 或 InteractionManager
 * 弹窗管理由调用方负责，不在此模块内处理
 */
export interface FetchOptions {
  /** 跳过去重，强制请求 */
  force?: boolean;
  /** 请求成功后重置页码到第 1 页（默认 true） */
  resetPage?: boolean;
  /** 成功后保留地图上已有的绘制图形 */
  preserveDrawings?: boolean;
}

export class DataSyncManager {
  private inFlightKey: { key: MapVariableListRequestKey; token: symbol } | null = null;
  private lastSuccessKey: MapVariableListRequestKey | null = null;

  constructor(
    private getMap: () => MapEngine,
    private clearPopup: () => void,
    private resetInteractionState: () => void,
    private highlightMap: (item: any) => void
  ) {}

  /**
   * 获取动态地图数据列表（带去重）。
   *
   * 内部维护两个 key：
   * - `inFlightKey`：当前正在进行的请求 key + token
   * - `lastSuccessKey`：最近一次成功请求的 key
   *
   * 当请求参数与上述任一 key 相同时，除非 `options.force`，否则跳过请求。
   *
   * @param positionVal - 位置信息，需包含 `lat`/`lon`（或 `latitude`/`longitude`）
   * @param searchformVal - 右侧查询条件（typeList / status / effectDateType / …）
   * @param options - 请求控制选项
   */
  async fetchList(positionVal: any, searchformVal: any, options?: FetchOptions) {
    const requestKey = buildRecordListRequestKey(positionVal, searchformVal);
    const shouldForce = options?.force === true;
    const shouldResetPage = options?.resetPage !== false;

    // 请求去重：与进行中 / 最近成功的 key 比较
    if (
      !shouldForce &&
      (isSameRecordListRequestKey(this.inFlightKey?.key ?? null, requestKey) ||
        isSameRecordListRequestKey(this.lastSuccessKey, requestKey))
    ) {
      return;
    }

    const requestToken = Symbol('mapVariableListRequest');
    this.inFlightKey = { key: requestKey, token: requestToken };

    try {
      const resp = await mapEditorFetch.dofetchRecordList({
        latitude: positionVal.lat,
        longitude: positionVal.lon,
        typeList: searchformVal?.typeList,
        status: searchformVal?.status,
        effectDateType: searchformVal?.effectDateType,
        remark: searchformVal?.remark,
        operator: searchformVal?.operator,
        effectVehicleType: searchformVal?.effectVehicleType,
      });

      if (resp.code === HttpStatusCode.Success) {
        this.lastSuccessKey = requestKey;

        if (!options?.preserveDrawings) {
          this.getMap().clearDraw?.();
        }

        const respMapId = resp?.data?.mapId;
        const respMapVersion = resp?.data?.mapVersion;

        const processedData = (resp.data?.variableList ?? []).map((v: any) => {
          const coordinates = v.geometry?.coordinates?.[0];
          this.getMap().drawPolygon(v.id, coordinates, v.type, v);
          return {
            ...v,
            coordinates,
          };
        });

        // 当响应中的 mapId/mapVersion 与当前搜索位置不一致时，同步更新
        const currentPositionSearchVal =
          useSearchStore.getState().positionSearchVal;
        const shouldUpdateMapMeta =
          respMapId != null &&
          respMapVersion != null &&
          (currentPositionSearchVal.mapId == null ||
            currentPositionSearchVal.mapVersion == null ||
            !Object.is(respMapId, currentPositionSearchVal.mapId) ||
            !Object.is(respMapVersion, currentPositionSearchVal.mapVersion) ||
            !isSameCoordinate(
              positionVal.lon ?? null,
              currentPositionSearchVal.lon ?? null
            ) ||
            !isSameCoordinate(
              positionVal.lat ?? null,
              currentPositionSearchVal.lat ?? null
            ));

        if (shouldUpdateMapMeta) {
          useSearchStore.getState().setPositionSearchVal((prev) => ({
            ...prev,
            ...positionVal,
            mapId: respMapId,
            mapVersion: respMapVersion,
          }));
        }

        useDataStore.getState().setPageData((prev) =>
          shouldResetPage ? { current: 1, pageSize: prev.pageSize } : prev
        );
        useDataStore.getState().setVariableList(processedData);
        return processedData;
      } else {
        message.error(resp?.message);
      }
    } catch (e) {
      console.error('fetchRecordList error', e);
    } finally {
      if (this.inFlightKey?.token === requestToken) {
        this.inFlightKey = null;
      }
    }
  }

  /** 创建动态地图 */
  async create(values: any): Promise<boolean> {
    const { session } = useEditorStore.getState();
    const draft = session.draft;
    const { feature } = draft || {};
    const positionSearchVal = useSearchStore.getState().positionSearchVal;
    const geometryGcj02 = feature && this.getMap().exportGeometry(feature);
    if (!positionSearchVal.mapId) {
      message.error('缺少地图 ID，无法创建动态地图');
      return false;
    }
    const data = buildMapVariableCreatePayload({
      values,
      geometryGcj02,
      mapId: positionSearchVal.mapId,
    });
    const res = await mapEditorFetch.addDynamicMap(data);
    if (res.code === HttpStatusCode.Success) {
      message.success('创建动态地图成功');
      if (draft?.id) {
        this.getMap().deletePolygon?.(draft.id);
      }
      useSearchStore.getState().setRightSearchVal(null);
      await this.fetchList(positionSearchVal, null, {
        force: true,
        resetPage: false,
      });
      useEditorStore.getState().setBrowseMode();
      return true;
    } else {
      message.error(res.message);
      return false;
    }
  }

  /** 创建轻图测试 */
  async createLightMap(payload: any): Promise<boolean> {
    const positionSearchVal = useSearchStore.getState().positionSearchVal;
    const res = await mapEditorFetch.addDynamicMap(payload);
    if (res.code === HttpStatusCode.Success) {
      message.success('创建轻图测试成功');
      useSearchStore.getState().setRightSearchVal(null);
      await this.fetchList(positionSearchVal, null, {
        force: true,
        resetPage: false,
      });
      return true;
    } else {
      message.error(res?.message || '创建轻图测试失败');
      return false;
    }
  }

  /** 编辑动态地图 */
  async edit(values: any): Promise<boolean> {
    const positionSearchVal = useSearchStore.getState().positionSearchVal;
    const res = await mapEditorFetch.editDynamicMap(values);
    if (res.code === HttpStatusCode.Success) {
      message.success('编辑动态地图成功');
      useSearchStore.getState().setRightSearchVal(null);
      await this.fetchList(positionSearchVal, null, {
        force: true,
        resetPage: false,
      });
      const activePolygonData = useEditorStore.getState().session.activePolygonData;
      const updatedItem = {
        ...activePolygonData,
        ...values,
      };
      useEditorStore.getState().updateActivePolygonData(updatedItem);
      useEditorStore.getState().setViewMode(updatedItem);
      this.highlightMap(updatedItem);
      return true;
    } else {
      message.error(res?.message);
      return false;
    }
  }

  /** 删除动态地图 */
  async remove(item: any) {
    const res = await mapEditorFetch.deleteMapVariable(item.id);
    if (res.code === HttpStatusCode.Success) {
      this.resetInteractionState();
      message.success('操作成功');
      const positionSearchVal = useSearchStore.getState().positionSearchVal;
      const rightSearchVal = useSearchStore.getState().rightSearchVal;
      await this.fetchList(positionSearchVal, rightSearchVal, {
        force: true,
        resetPage: false,
      });
      useEditorStore.getState().setBrowseMode();
    } else {
      message.error(res?.message);
    }
  }

  /** 启停用动态地图
   * 与 master 对齐：成功后清空弹窗与选中态，保留当前分页刷新列表
   */
  async toggleStatus(item: any) {
    const nextStatus =
      item.status === MapStatus.RS_INUSE
        ? MapStatus.RS_DISCARDED
        : MapStatus.RS_INUSE;
    const res = await mapEditorFetch.editEnable({
      id: item.id,
      status: nextStatus,
    });
    if (res.code === HttpStatusCode.Success) {
      this.resetInteractionState();
      const positionSearchVal = useSearchStore.getState().positionSearchVal;
      const rightSearchVal = useSearchStore.getState().rightSearchVal;
      await this.fetchList(
        positionSearchVal,
        rightSearchVal,
        { force: true, resetPage: false }
      );
      useEditorStore.getState().setBrowseMode();
      message.success('操作成功');
    } else {
      message.error(res?.message);
    }
  }
}
